import invariant from 'invariant';

import { BrandingModelSelectorType } from 'src/models/Branding/BrandingModel';
import { CommonCurrentPartnerType } from 'src/models/CurrentPartner';
import { FrontConfigurationType } from 'src/models/FrontendConfiguration';
import { LookupRegionsType } from 'src/models/Lookup';
import { SidebarModelResponse, SidebarModelType } from 'src/models/Partner/Sidebar';
import { InitialCommonStateType } from 'src/store/commonPart/slice';
import { RootState } from 'src/store/store';
import { BaseStoreEntityType } from 'src/store/types';

/** common data */
export const commonSelector = ({ common }: RootState): InitialCommonStateType => common;

/** percentage of loading the app */
export const requiredDataLoaded = ({ common }: RootState): boolean => common.percentLoading === 100;
export const requiredDataPercentLoaded = ({ common }: RootState): number => common.percentLoading;
export const requiredDataHasErrors = ({ common }: RootState): boolean => common.percentErrors.length > 0;

/** end percentage of loading the app */

export const showGlobalLoaderSelector = ({ common: { showLoader } }: RootState): boolean => showLoader;

export const deCommonCurrentPartnerSelector = ({
  common: {
    currentPartner: { response },
  },
}: RootState): CommonCurrentPartnerType => {
  invariant(response, "the currentPartner store isn't loaded");
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return response!;
};

export const commonCurrentPartnerSelector = ({
  common: { currentPartner },
}: RootState): RootState['common']['currentPartner'] => currentPartner;

export const commonCurrencyListSelector = ({
  common: { currencyList },
}: RootState): RootState['common']['currencyList'] => currencyList;

export const commonTimezoneListSelector = ({
  common: { timezonesList },
}: RootState): RootState['common']['timezonesList'] => timezonesList;

/** countries */
export const commonCountriesListSelector = ({
  common: { countriesList },
}: RootState): RootState['common']['countriesList'] => countriesList;

/** regions by country id */
export const commonRegionsByCountryIdSelector =
  (countryId: string) =>
  ({ common: { regionsMap } }: RootState): BaseStoreEntityType<LookupRegionsType> => ({
    error: regionsMap.error,
    loading: regionsMap.loading,
    response: regionsMap.response[countryId] || null,
  });

/** configuration data */
export const deConfigurationSettingsSelector = ({
  common: {
    configuration: { response },
  },
}: RootState): FrontConfigurationType & {
  RecaptchaSiteKey: string;
} => {
  invariant(response, "the configuration store isn't loaded");

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return response!;
};

export const configurationLoadedSelector = ({
  common: {
    configuration: { loading },
  },
}: RootState) => !loading;

export const leftMenuDataSelector = ({ common }: RootState): SidebarModelType | null => common.sidebar.response;

export const SidebarSelector = ({ common }: RootState): SidebarModelResponse | null => common.globalNavigation.response;
export const showGlobalNavigationSelector = ({
  common,
}: RootState): SidebarModelResponse['IsGlobalNavigationEnabled'] =>
  !!common.globalNavigation.response?.IsGlobalNavigationEnabled;

export const brandingModelSelector = ({
  common: { brandingModel, brandingModelLoading },
}: RootState): BrandingModelSelectorType => ({
  data: brandingModel,
  loading: brandingModelLoading,
});

export const getReleaseFeatureFlagSelector =
  (flagName: string) =>
  ({ common }: RootState): InitialCommonStateType['releaseFeatureFlags'][0] | null =>
    common.releaseFeatureFlags.find(({ name }) => name === flagName) || null;

export const getExperimentalFeatureFlagSelector =
  (flagName: string) =>
  ({ common }: RootState): InitialCommonStateType['experimentalFeatureFlags'][0] | null =>
    common.experimentalFeatureFlags.find(({ name }) => name === flagName) || null;

export const canCreateCampaignFromTemplateSelector = ({ common }: RootState) => common.canCreateCampaignFromTemplate;
export const showTemplateGalleryModalSelector = ({
  common,
}: RootState): {
  show: InitialCommonStateType['showTemplateGalleryModal'];
  calledFrom: InitialCommonStateType['templateGalleryModalCalledFrom'];
} => ({
  show: common.showTemplateGalleryModal,
  calledFrom: common.templateGalleryModalCalledFrom,
});

export const showTemplateGalleryModalDetailsIdSelector = ({ common }: RootState) =>
  common.showTemplateGalleryModalDetailsId;

export const shouldRedirectToStopPageSelector = ({ common }: RootState) => common.shouldRedirectToStopPage;
export const showPersonalOrBusinessForBusinessModalSelector = ({ common }: RootState) =>
  common.showPersonalOrBusinessForBusinessModal;

export const expiredCampaignsModalSelector = ({ common }: RootState) => common.expiredCampaignsModal;
